import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "~/components/ui/toaster";
import { ScaleControl, useMap, type MapMouseEvent } from "react-map-gl/mapbox";
import type { MapMouseEvent as MapboxMapMouseEvent } from "mapbox-gl";
import {
  addRelatedPlace,
  createCuratorProfile,
  createPlace,
  getApiErrorMessage,
  getPlaceTrack,
  isAccessRevokedError,
  isCuratorProfileRequiredError,
  isNotFoundError,
  PLACE_TYPE_META,
  relatedPlaceToPreview,
  removeRelatedPlace,
  transferMapOwnership,
  transferPlaceOwnership,
  type PlaceFeature,
  type PlacePreviewFeature,
  type PlaceType,
  type RelatedPlace,
} from "../lib/api";
import { FLY_TO_ZOOM, FIT_BOUNDS_PADDING, ADVENTURE_MAP_PLACES_LAYER_ID, PLACES_LAYER_ID } from "../lib/mapConstants";
import { routes } from "../lib/routes";
import { useAdventureMap } from "../lib/adventureMapStore";
import { useMapSelection } from "../lib/mapSelectionStore";
import { useEditorMode } from "../lib/editorModeStore";
import { usePlaces } from "../lib/placesStore";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { usePlaceNavigation } from "../hooks/usePlaceNavigation";
import { Map, useViewport, usePanel, usePanelPosition } from "../components/map";
import { toDetailLevel } from "~/lib/detailLevel";
import { MapHint } from "../components/ui/map-hint";
import { EditorPanelContent } from "../components/builder";
import { MapPopup, MapMarker } from "../components/map";
import PlaceTypeMenu from "../components/place/PlaceTypeMenu";
import PlaceForm from "../components/place/PlaceForm";
import { PlaceTypePicker } from "../components/place/PlaceTypePicker";
import { POIContextMenu } from "../components/place/POIContextMenu";
import { CuratorProfileDialog } from "../components/CuratorProfileDialog";
import ShareDialog from "../components/ShareDialog";
import CollaboratorsDialog from "../components/CollaboratorsDialog";
import TransferOwnershipDialog from "../components/TransferOwnershipDialog";
import { PlaceTypeIcon } from "../components/ui/icons/place-type-icons";
import { Card } from "../components/ui/card";
import {
  MapSearchBox,
  type MapSearchBoxRef,
  SearchResultMarkers,
  filterBySearchResults,
} from "../components/search-box";
import { mapPlaceFeatureFromMapboxPoi } from "../lib/mapboxUtils";
import {
  AdventureMapPlacesLayer,
  CompassControl,
  RelatedPlacesLayer,
  RouteTrackLayer,
  SelectedPlaceLayer,
  MapboxPoiLayer,
  MapboxTrailsLayer,
  PlacesLayer,
  ZoomControls,
} from "../components/map";
import { useMapEvent } from "../components/map/hooks/useMapEvent";

export default function MapEditor() {
  const { id: mapId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { below } = useBreakpoint();
  const isMobile = below("md");
  const instrumentation = useInstrumentation();
  const { current: mapInstance } = useMap();
  const { flyTo, fitBounds, isReady } = useViewport();
  const { position, snapTo } = usePanel();
  const searchBoxRef = useRef<MapSearchBoxRef>(null);

  // Data stores
  const {
    map: adventureMap,
    places: adventureMapPlaces,
    loading,
    load: loadAdventureMap,
    update: updateAdventureMap,
    patch,
    createAndAddToMap,
    updatePlace,
    deletePlace,
    publishPlace,
    addPlaceToMap,
    reloadPlaces,
    reset: resetAdventureMap,
  } = useAdventureMap();

  const { places, reload: reloadCatalog } = usePlaces();
  const { selected: selectedPlace, select: selectPlace, deselect: deselectPlace } = useMapSelection();
  const {
    mode: editorMode,
    placingContext,
    linkingContext,
    startPlacing,
    setPlacingContext,
    cancelPlacing,
    setLinkingContext,
    cancelLinking,
    reset: resetEditorMode,
  } = useEditorMode();

  // Local UI state
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [savingPlace, setSavingPlace] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [collaboratorsDialogOpen, setCollaboratorsDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [placeTransferDialogOpen, setPlaceTransferDialogOpen] = useState(false);
  const [curatorDialogOpen, setCuratorDialogOpen] = useState(false);
  const [savingCurator, setSavingCurator] = useState(false);
  const [clickPopupCoords, setClickPopupCoords] = useState<[number, number] | null>(null);
  const [poiPopup, setPoiPopup] = useState<{
    name: string;
    coordinates: [number, number];
    suggestedType: PlaceType | null;
  } | null>(null);
  const [searchResults, setSearchResults] = useState<PlacePreviewFeature[]>([]);
  const [autoFocusPlace, setAutoFocusPlace] = useState(false);

  // Derive mode states
  const isPlacing = editorMode === "placing";
  const isLinking = editorMode === "linking";

  // Declarative panel position control
  usePanelPosition("preview", !!selectedPlace);
  usePanelPosition("collapsed", isPlacing);
  usePanelPosition("collapsed", isLinking);

  const details = toDetailLevel(position);

  // Navigation callback: handles place ID (from URL) or preview feature (from history state)
  const handlePlaceChange = useCallback(
    (data: string | PlacePreviewFeature | null) => {
      if (!data) {
        deselectPlace();
        return;
      }

      // Preview feature from history state - use directly
      if (typeof data !== "string") {
        selectPlace(data);
        return;
      }

      // Place ID from URL - look up the feature
      const adventurePlace = adventureMapPlaces.features.find((f) => f.id === data);
      if (adventurePlace) {
        selectPlace(adventurePlace);
        return;
      }
      const globalPlace = places.features.find((f) => f.id === data);
      if (globalPlace) {
        selectPlace(globalPlace);
      }
    },
    [adventureMapPlaces.features, places.features, selectPlace, deselectPlace],
  );

  const {
    navigateToPlace: navigateToPlaceBase,
    clearPlaceSelection,
    originPlaceId,
  } = usePlaceNavigation({
    selectedPlaceId: selectedPlace?.properties.id ?? null,
    onPlaceChange: handlePlaceChange,
  });

  const handleBack = useCallback(() => {
    if (originPlaceId) {
      navigate(-1);
    } else {
      clearPlaceSelection();
    }
  }, [originPlaceId, navigate, clearPlaceSelection]);

  // Wrapper that handles focus management for keyboard navigation
  const navigateToPlace = useCallback(
    (
      feature: PlacePreviewFeature,
      options?: { replace?: boolean; fromKeyboard?: boolean; originPlaceId?: string; preview?: boolean },
    ) => {
      navigateToPlaceBase(feature, options);
      setAutoFocusPlace(!!options?.fromKeyboard);
    },
    [navigateToPlaceBase],
  );

  // Re-sync URL → state when places load (initial load may happen before places are fetched)
  const [searchParams] = useSearchParams();
  const urlPlaceId = searchParams.get("place");
  useEffect(() => {
    if (!urlPlaceId || selectedPlace) return;
    handlePlaceChange(urlPlaceId);
  }, [urlPlaceId, selectedPlace, adventureMapPlaces.features, places.features, handlePlaceChange]);

  // Fly to selected place when it changes (includes related places in viewport if any)
  useEffect(() => {
    if (!selectedPlace || !isReady) return;
    const center = selectedPlace.geometry.coordinates as [number, number];
    const fitWith =
      selectedPlace.properties.relatedPlaces?.map((p) => [p.longitude, p.latitude] as [number, number]) ?? [];
    flyTo(center, { zoom: FLY_TO_ZOOM, fitWith });
  }, [selectedPlace, isReady, flyTo]);

  // Load map data and places
  useEffect(() => {
    if (!mapId) {
      navigate(routes.to.maps.root(), { replace: true });
      return;
    }

    loadAdventureMap(mapId)
      .then(() => {
        const { map, places } = useAdventureMap.getState();
        if (map) {
          const daysSinceCreation = Math.floor(
            (Date.now() - new Date(map.createdAt).getTime()) / (1000 * 60 * 60 * 24),
          );
          instrumentation.track("Map Editor Opened", {
            mapId: map.id,
            placeCount: places.features.length,
            daysSinceCreation,
          });
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Map not found");
        navigate(routes.to.maps.root(), { replace: true });
      });

    return () => {
      resetAdventureMap();
      resetEditorMode();
    };
  }, [mapId, loadAdventureMap, resetAdventureMap, resetEditorMode, navigate, instrumentation]);

  // Fit map bounds on initial load only
  const hasFittedBounds = useRef(false);
  useEffect(() => {
    hasFittedBounds.current = false;
  }, [mapId]);

  useEffect(() => {
    if (!hasFittedBounds.current && isReady && adventureMapPlaces.features.length > 0) {
      hasFittedBounds.current = true;
      fitBounds(adventureMapPlaces, isMobile ? FIT_BOUNDS_PADDING.mobile : FIT_BOUNDS_PADDING.desktop);
    }
  }, [adventureMapPlaces, fitBounds, isMobile, isReady]);

  // Handle adding a related place (when in linking mode)
  const handleAddRelatedPlace = useCallback(
    async (relatedPlaceId: string) => {
      if (!mapId || !linkingContext) return;
      const sourcePlaceId = linkingContext.sourcePlaceId;
      if (relatedPlaceId === sourcePlaceId) return; // Can't link to self
      try {
        await addRelatedPlace(mapId, sourcePlaceId, relatedPlaceId);
        await reloadPlaces(mapId);
        // Re-select from fresh data to update relatedPlaces in the UI
        const freshPlace = useAdventureMap.getState().places.features.find((f) => f.properties.id === sourcePlaceId);
        if (freshPlace) selectPlace(freshPlace);
        cancelLinking();
        toast.success("Related place added");
      } catch {
        toast.error("Failed to add related place");
      }
    },
    [mapId, linkingContext, reloadPlaces, selectPlace, cancelLinking],
  );

  // Handle click on adventure map places layer
  const handleAdventureMapPlaceClick = useCallback(
    (feature: PlaceFeature) => {
      if (isPlacing) return;
      setPoiPopup(null);
      if (isLinking) {
        handleAddRelatedPlace(feature.properties.id);
        return;
      }
      navigateToPlace(feature);
    },
    [isPlacing, isLinking, handleAddRelatedPlace, navigateToPlace],
  );

  // Handle click on global places layer
  const handlePlaceClick = useCallback(
    (e: MapMouseEvent) => {
      if (isPlacing) return;
      const clickedFeature = e.features?.[0];
      const placeId = clickedFeature?.properties?.id as string | undefined;
      if (!placeId) return;

      const feature = places.features.find((f) => f.properties.id === placeId);
      if (feature) {
        setPoiPopup(null);
        if (isLinking) {
          handleAddRelatedPlace(placeId);
          return;
        }
        navigateToPlace(feature);
      }
    },
    [isPlacing, isLinking, places.features, handleAddRelatedPlace, navigateToPlace],
  );
  useMapEvent("click", PLACES_LAYER_ID, handlePlaceClick);

  // Handle click on Mapbox POI labels - show in panel
  const handlePoiClick = useCallback(
    (event: { name: string; coordinates: [number, number]; suggestedType: PlaceType | null }) => {
      if (isPlacing && placingContext?.type && !placingContext.coordinates) {
        setPlacingContext({ coordinates: event.coordinates, name: event.name });
        return;
      }
      if (isLinking && !linkingContext?.coordinates) {
        setLinkingContext({ coordinates: event.coordinates, name: event.name });
        return;
      }
      setClickPopupCoords(null);
      setPoiPopup(null);
      const feature = mapPlaceFeatureFromMapboxPoi({
        name: event.name,
        coordinates: event.coordinates,
        suggestedType: event.suggestedType,
      });
      navigateToPlace(feature);
    },
    [isPlacing, isLinking, placingContext, linkingContext, setPlacingContext, setLinkingContext, navigateToPlace],
  );

  // Handle context menu (right-click) on Mapbox POI labels - show popup
  const handlePoiContextMenu = useCallback(
    (event: { name: string; coordinates: [number, number]; suggestedType: PlaceType | null }) => {
      if (isPlacing) return;
      setClickPopupCoords(null);
      setPoiPopup(event);
    },
    [isPlacing],
  );

  // Handle click on trail layer
  const handleTrailClick = useCallback(
    (event: { name: string | null; coordinates: [number, number]; suggestedType: PlaceType }) => {
      if (isPlacing) return;
      setClickPopupCoords(null);
      setPoiPopup({
        name: event.name ?? "",
        coordinates: event.coordinates,
        suggestedType: event.suggestedType,
      });
    },
    [isPlacing],
  );

  // Handle map click: in placing mode set coordinates, in linking mode show form, otherwise close popups
  const handleMapClick = useCallback(
    (e: MapboxMapMouseEvent) => {
      const { mode, placingContext: ctx, linkingContext: linkCtx } = useEditorMode.getState();
      // Set coordinates if we have a type but no coordinates yet
      if (mode === "placing" && ctx?.type && !ctx.coordinates) {
        setPlacingContext({ coordinates: [e.lngLat.lng, e.lngLat.lat] });
        return;
      }

      // Check if we clicked on a place layer
      const placeFeatures = mapInstance?.queryRenderedFeatures(e.point, {
        layers: [PLACES_LAYER_ID, ADVENTURE_MAP_PLACES_LAYER_ID].filter((id) => mapInstance?.getLayer(id)),
      });
      if (placeFeatures?.length) return;

      // In linking mode, clicking empty map opens the form to create a new place inline
      if (mode === "linking" && linkCtx) {
        setLinkingContext({ coordinates: [e.lngLat.lng, e.lngLat.lat] });
        return;
      }

      // Close popups when clicking empty space
      setClickPopupCoords(null);
      setPoiPopup(null);
    },
    [mapInstance, setPlacingContext, setLinkingContext],
  );

  // Handle contextmenu (right-click / long-press): show type picker popup
  const handleMapContextMenu = useCallback(
    (e: MapboxMapMouseEvent) => {
      if (useEditorMode.getState().mode === "placing" || !mapId) return;
      const features = mapInstance?.queryRenderedFeatures(e.point, {
        layers: [PLACES_LAYER_ID, ADVENTURE_MAP_PLACES_LAYER_ID].filter((id) => mapInstance?.getLayer(id)),
      });
      if (!features?.length) {
        e.originalEvent?.preventDefault?.();
        setPoiPopup(null);
        setClickPopupCoords([e.lngLat.lng, e.lngLat.lat]);
      }
    },
    [mapId, mapInstance],
  );

  // Attach map event listeners
  useEffect(() => {
    if (!mapInstance) return;

    mapInstance.on("click", handleMapClick);
    mapInstance.on("contextmenu", handleMapContextMenu);

    return () => {
      mapInstance.off("click", handleMapClick);
      mapInstance.off("contextmenu", handleMapContextMenu);
    };
  }, [mapInstance, handleMapClick, handleMapContextMenu]);

  // Crosshair cursor when clicking map will place something
  useEffect(() => {
    if (!mapInstance) return;

    const placingNeedsCoords = isPlacing && placingContext?.type && !placingContext.coordinates;
    const linkingNeedsCoords = isLinking && !linkingContext?.coordinates;
    if (placingNeedsCoords || linkingNeedsCoords) {
      mapInstance.getCanvas().style.cursor = "crosshair";
    }

    return () => {
      const canvas = mapInstance.getCanvas();
      if (canvas?.style) canvas.style.cursor = "";
    };
  }, [
    mapInstance,
    isPlacing,
    isLinking,
    placingContext?.type,
    placingContext?.coordinates,
    linkingContext?.coordinates,
  ]);

  // Cancel add mode
  const cancelAddMode = useCallback(() => {
    cancelPlacing();
    setShowTypeMenu(false);
    setClickPopupCoords(null);
  }, [cancelPlacing]);

  // Handle "Add to map" from POI context menu
  const handlePOIAddPlace = useCallback(() => {
    if (!poiPopup) return;
    startPlacing({
      coordinates: poiPopup.coordinates,
      name: poiPopup.name,
      type: poiPopup.suggestedType ?? undefined,
    });
    setPoiPopup(null);
  }, [poiPopup, startPlacing]);

  // Handle type selection from click popup
  const handlePopupTypeSelect = useCallback(
    (type: PlaceType) => {
      if (!clickPopupCoords) return;
      startPlacing({ type, coordinates: clickPopupCoords });
      setClickPopupCoords(null);
    },
    [clickPopupCoords, startPlacing],
  );

  const closeClickPopup = useCallback(() => {
    setClickPopupCoords(null);
  }, []);

  const closePoiPopup = useCallback(() => {
    setPoiPopup(null);
  }, []);

  // Escape key to cancel add mode, linking mode, or close type menu
  useEffect(() => {
    if (!showTypeMenu && !isPlacing && !isLinking) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isLinking) {
          cancelLinking();
        } else {
          cancelAddMode();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showTypeMenu, isPlacing, isLinking, cancelAddMode, cancelLinking]);

  // Place type selection handler (from FAB)
  const handleTypeSelect = (type: PlaceType) => {
    setShowTypeMenu(false);
    startPlacing({ type });
  };

  // Submit new place
  const handlePlaceSubmit = async (data: { name: string; type: PlaceType; description?: string }) => {
    if (!mapId || !placingContext?.coordinates) return;

    setSavingPlace(true);
    try {
      const place = await createAndAddToMap(mapId, {
        name: data.name,
        type: data.type,
        longitude: placingContext.coordinates[0],
        latitude: placingContext.coordinates[1],
        description: data.description,
      });
      await reloadCatalog();
      toast.success("Place added");
      searchBoxRef.current?.clear();
      cancelAddMode();
      // Get the fresh feature from the store
      const freshPlace = useAdventureMap.getState().places.features.find((f) => f.id === place.id);
      if (freshPlace) navigateToPlace(freshPlace);
    } catch {
      toast.error("Failed to add place");
    } finally {
      setSavingPlace(false);
    }
  };

  // Submit new place inline as related (linking mode)
  const handleLinkingFormSubmit = async (data: { name: string; type: PlaceType; description?: string }) => {
    if (!mapId || !linkingContext?.coordinates) return;

    setSavingPlace(true);
    try {
      const sourcePlaceId = linkingContext.sourcePlaceId;
      // Create place (not added to map)
      const place = await createPlace({
        name: data.name,
        type: data.type,
        longitude: linkingContext.coordinates[0],
        latitude: linkingContext.coordinates[1],
        description: data.description,
        source: "inline_related",
      });
      // Link it as related
      await addRelatedPlace(mapId, sourcePlaceId, place.id);
      // Refresh stores
      await reloadPlaces(mapId);
      await reloadCatalog();
      // Re-select from fresh data to show updated related places
      const freshPlace = useAdventureMap.getState().places.features.find((f) => f.properties.id === sourcePlaceId);
      if (freshPlace) selectPlace(freshPlace);
      toast.success("Related place added");
      cancelLinking();
    } catch {
      toast.error("Failed to add related place");
    } finally {
      setSavingPlace(false);
    }
  };

  // Cancel linking form (close form but stay in linking mode)
  const handleLinkingFormCancel = () => {
    setLinkingContext({ coordinates: undefined });
  };

  // Handle access revoked error (403/404) - show toast and redirect
  const handleAccessRevoked = useCallback(() => {
    toast.error("You no longer have access to this map");
    navigate(routes.to.maps.root(), { replace: true });
  }, [navigate]);

  // Handle place update
  const handlePlaceUpdate = async (data: Parameters<typeof updatePlace>[2]) => {
    if (!mapId || !selectedPlace) return;
    const placeId = selectedPlace.properties.id;
    try {
      await updatePlace(mapId, placeId, data);
      await reloadCatalog();
      // Re-select from fresh data (map places or catalog)
      const freshPlace =
        useAdventureMap.getState().places.features.find((f) => f.id === placeId) ??
        usePlaces.getState().places.features.find((f) => f.id === placeId);
      if (freshPlace) selectPlace(freshPlace);
      toast.success("Place updated");
    } catch (err) {
      if (isAccessRevokedError(err)) {
        handleAccessRevoked();
        return;
      }
      // Re-throw for inline error display in PlaceDetailView
      throw err;
    }
  };

  // Handle place removal from map
  const handlePlaceDelete = async () => {
    if (!mapId || !selectedPlace) return;
    const placeId = selectedPlace.id as string;
    try {
      await deletePlace(mapId, placeId);
      // Re-select from global places to clear map-specific data (relatedPlaces)
      const globalPlace = places.features.find((f) => f.id === placeId);
      if (globalPlace) {
        selectPlace(globalPlace);
      }
      toast.success("Removed from map");
    } catch (err) {
      if (isAccessRevokedError(err)) {
        handleAccessRevoked();
      } else {
        toast.error("Failed to remove place");
      }
    }
  };

  // Handle place images changed - reload and re-select
  const handlePlaceImagesChanged = useCallback(async () => {
    if (!mapId || !selectedPlace) return;
    const placeId = selectedPlace.properties.id;
    await reloadPlaces(mapId);
    await reloadCatalog();
    const freshPlace =
      useAdventureMap.getState().places.features.find((f) => f.id === placeId) ??
      usePlaces.getState().places.features.find((f) => f.id === placeId);
    if (freshPlace) selectPlace(freshPlace);
  }, [mapId, selectedPlace, reloadPlaces, reloadCatalog, selectPlace]);

  // Handle related place removal
  const handleRelatedPlaceRemove = useCallback(
    async (relatedPlaceId: string) => {
      if (!mapId || !selectedPlace) return;
      const placeId = selectedPlace.properties.id;
      await removeRelatedPlace(mapId, placeId, relatedPlaceId);
      await reloadPlaces(mapId);
      // Re-select from fresh data to update relatedPlaces in the UI
      const freshPlace = useAdventureMap.getState().places.features.find((f) => f.properties.id === placeId);
      if (freshPlace) selectPlace(freshPlace);
    },
    [mapId, selectedPlace, reloadPlaces, selectPlace],
  );

  // Handle related place selection (from map layer click)
  const handleSelectRelatedPlace = useCallback(
    (relatedPlace: RelatedPlace) => {
      snapTo("preview");
      const feature =
        adventureMapPlaces.features.find((f) => f.properties.id === relatedPlace.id) ??
        places.features.find((f) => f.properties.id === relatedPlace.id) ??
        relatedPlaceToPreview(relatedPlace);
      navigateToPlace(feature, { originPlaceId: selectedPlace?.properties.id, preview: true });
    },
    [snapTo, adventureMapPlaces.features, places.features, navigateToPlace, selectedPlace],
  );

  // Handle place publish
  const handlePlacePublish = async () => {
    if (!mapId || !selectedPlace) return;
    const placeId = selectedPlace.properties.id;
    try {
      const published = await publishPlace(mapId, placeId);
      await reloadCatalog();
      // Navigate to the (potentially new) place ID, replacing current history entry
      const freshPlaces = useAdventureMap.getState().places;
      const freshPlace = freshPlaces.features.find((f) => f.properties.id === published.id);
      if (freshPlace) navigateToPlace(freshPlace, { replace: true });
      toast.success("Place published");
    } catch (err) {
      if (isAccessRevokedError(err)) {
        handleAccessRevoked();
      } else if (isCuratorProfileRequiredError(err)) {
        setCuratorDialogOpen(true);
      } else {
        toast.error("Failed to publish place");
      }
    }
  };

  // Handle adding a place to the adventure map (preview mode creates new, existing adds reference)
  const handleAddPlaceToAdventureMap = async () => {
    if (!mapId || !selectedPlace) return;

    const { id, name, type } = selectedPlace.properties;
    const [longitude, latitude] = selectedPlace.geometry.coordinates;

    try {
      if (!id && !type) {
        // Preview mode without type - show form to pick type
        startPlacing({ coordinates: [longitude, latitude], name });
      } else if (!id) {
        // Preview mode with known type - create directly
        const place = await createAndAddToMap(mapId, { name, type: type!, longitude, latitude });
        await reloadCatalog();
        toast.success("Place added");
        // Get the fresh feature from the store
        const freshPlace = useAdventureMap.getState().places.features.find((f) => f.id === place.id);
        if (freshPlace) navigateToPlace(freshPlace);
      } else {
        // Add existing place to map
        await addPlaceToMap(mapId, id);
        toast.success("Added to map");
      }
    } catch (err) {
      if (isAccessRevokedError(err)) {
        handleAccessRevoked();
      } else {
        toast.error("Failed to add place");
      }
    }
  };

  // Handle curator profile creation
  const handleCuratorCreate = async (data: { displayName: string }) => {
    setSavingCurator(true);
    try {
      await createCuratorProfile({ ...data, trigger: "publish_place" });
      setCuratorDialogOpen(false);
      await handlePlacePublish();
    } catch {
      toast.error("Failed to create curator profile");
    } finally {
      setSavingCurator(false);
    }
  };

  const handleMapTransfer = async (email: string) => {
    if (!adventureMap) return;
    try {
      await transferMapOwnership(adventureMap.id, email);
      toast.success("Ownership transferred");
      await loadAdventureMap(adventureMap.id);
    } catch (err) {
      throw new Error(transferErrorMessage(err));
    }
  };

  const handlePlaceTransfer = async (email: string) => {
    if (!adventureMap || !selectedPlace) return;
    try {
      await transferPlaceOwnership(selectedPlace.properties.id, email);
      toast.success("Ownership transferred");
      await reloadPlaces(adventureMap.id);
    } catch (err) {
      throw new Error(transferErrorMessage(err));
    }
  };

  const handleMapUpdate = async (data: { name?: string; description?: string | null }) => {
    if (!mapId || !adventureMap) return;
    try {
      await updateAdventureMap(mapId, data);
      toast.success("Map updated");
    } catch (err) {
      if (isAccessRevokedError(err)) {
        handleAccessRevoked();
      } else {
        toast.error("Failed to update map");
      }
    }
  };

  // Filter global places to exclude ones already on this map (to avoid duplicate markers)
  const filteredGlobalPlaces = useMemo(() => {
    const onMapPlaceIds = new Set(adventureMapPlaces.features.map((f) => f.properties.id));
    return {
      ...places,
      features: places.features.filter((f) => !onMapPlaceIds.has(f.properties.id)),
    };
  }, [places, adventureMapPlaces.features]);

  // Merge places for search (adventure map + global)
  const searchablePlaces = useMemo(
    () => [...adventureMapPlaces.features, ...filteredGlobalPlaces.features],
    [adventureMapPlaces.features, filteredGlobalPlaces.features],
  );

  // Filter layers when search is active (hide places shown in search results)
  // Also filter by allowed types when in linking mode
  const adventureMapPlacesForDisplay = useMemo(() => {
    let filtered = filterBySearchResults(adventureMapPlaces, searchResults);
    if (isLinking && linkingContext?.allowedTypes) {
      filtered = {
        ...filtered,
        features: filtered.features.filter((f) => linkingContext.allowedTypes!.includes(f.properties.type)),
      };
    }
    return filtered;
  }, [adventureMapPlaces, searchResults, isLinking, linkingContext?.allowedTypes]);

  const placesForDisplay = useMemo(() => {
    let filtered = filterBySearchResults(filteredGlobalPlaces, searchResults);
    if (isLinking && linkingContext?.allowedTypes) {
      filtered = {
        ...filtered,
        features: filtered.features.filter((f) => linkingContext.allowedTypes!.includes(f.properties.type)),
      };
    }
    return filtered;
  }, [filteredGlobalPlaces, searchResults, isLinking, linkingContext?.allowedTypes]);

  // Check if selected place is from global places (not yet on this map)
  const isSelectedPlaceFromGlobal = useMemo(() => {
    if (!selectedPlace) return false;
    return !adventureMapPlaces.features.some((f) => f.id === selectedPlace.id);
  }, [selectedPlace, adventureMapPlaces.features]);

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
        <Card className="p-8 text-center text-muted-foreground pointer-events-auto">Loading map...</Card>
      </div>
    );
  }

  if (!adventureMap) {
    return null;
  }

  return (
    <>
      {/* Global places layer (filtered to exclude places already on this map and search results) */}
      <PlacesLayer places={placesForDisplay} excludeIds={selectedPlace?.properties.relatedPlaces?.map((p) => p.id)} />

      {/* Scale control (stays in mapbox layer) */}
      <ScaleControl position="bottom-left" maxWidth={100} unit="metric" />

      {/* Route track for selected place (render first = bottom layer) */}
      <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />

      {/* Adventure map places layer (highlighted icons, filtered when searching) */}
      <AdventureMapPlacesLayer
        places={adventureMapPlacesForDisplay}
        excludeIds={selectedPlace?.properties.relatedPlaces?.map((p) => p.id)}
        onClick={handleAdventureMapPlaceClick}
      />

      {/* Related places layer (smaller pins showing context around selected place) */}
      <RelatedPlacesLayer
        relatedPlaces={selectedPlace?.properties.relatedPlaces ?? []}
        onClick={handleSelectRelatedPlace}
      />

      {/* Selected place layer */}
      <SelectedPlaceLayer place={selectedPlace} />

      {/* Mapbox POI click handler */}
      <MapboxPoiLayer enabled={!isPlacing} onClick={handlePoiClick} onContextMenu={handlePoiContextMenu} />

      {/* Mapbox trail click handler */}
      <MapboxTrailsLayer enabled={!isPlacing} onClick={handleTrailClick} onContextMenu={handleTrailClick} />

      {/* Search box in header */}
      <Map.HeaderLeft>
        <MapSearchBox
          ref={searchBoxRef}
          map={mapInstance?.getMap() ?? null}
          className="flex-1 max-w-80"
          places={searchablePlaces}
          onSearchResults={setSearchResults}
          onFeatureSelect={navigateToPlace}
        />
      </Map.HeaderLeft>

      {/* Search result markers (rendered by consumer for layer control) */}
      <SearchResultMarkers
        map={mapInstance?.getMap() ?? null}
        features={searchResults}
        excludeCoordinates={selectedPlace?.geometry.coordinates as [number, number] | undefined}
        onSelect={navigateToPlace}
      />

      {/* Side panel (desktop) / Bottom sheet (mobile) */}
      <Map.Panel>
        <EditorPanelContent
          map={adventureMap}
          adventureMapPlaces={adventureMapPlaces}
          selectedPlaceId={selectedPlace?.id?.toString() ?? null}
          selectedPlace={isSelectedPlaceFromGlobal ? selectedPlace : null}
          details={details}
          autoFocusPlace={autoFocusPlace}
          onPlaceDeselect={handleBack}
          onMapUpdate={handleMapUpdate}
          onShare={adventureMap.permissions.canShare ? () => setShareDialogOpen(true) : undefined}
          onCollaborators={
            adventureMap.permissions.canManageCollaborators ? () => setCollaboratorsDialogOpen(true) : undefined
          }
          onTransfer={adventureMap.permissions.canTransfer ? () => setTransferDialogOpen(true) : undefined}
          canPublishLandingPage={adventureMap.permissions.canPublishLandingPage}
          onPlaceUpdate={handlePlaceUpdate}
          onPlaceDelete={handlePlaceDelete}
          onPlacePublish={handlePlacePublish}
          onPlaceTransfer={() => setPlaceTransferDialogOpen(true)}
          onPlaceAdd={handleAddPlaceToAdventureMap}
          onPlaceImageAdded={handlePlaceImagesChanged}
          onPlaceImageDeleted={handlePlaceImagesChanged}
          onPlaceTrackUploaded={handlePlaceImagesChanged}
          onPlaceTrackDeleted={handlePlaceImagesChanged}
          onRelatedPlaceRemove={handleRelatedPlaceRemove}
          onSelectRelatedPlace={handleSelectRelatedPlace}
          onAccessRevoked={handleAccessRevoked}
        />
      </Map.Panel>

      {/* Controls region */}
      <Map.Controls>
        <CompassControl />
        <ZoomControls />
        <PlaceTypeMenu open={showTypeMenu} onOpenChange={setShowTypeMenu} onSelect={handleTypeSelect} />
      </Map.Controls>

      {/* Click-to-add type picker popup */}
      <MapPopup map={mapInstance?.getMap() ?? null} coordinates={clickPopupCoords} onClose={closeClickPopup}>
        {clickPopupCoords && <PlaceTypePicker onSelect={handlePopupTypeSelect} onClose={closeClickPopup} />}
      </MapPopup>

      {/* POI context menu popup */}
      <MapPopup
        map={mapInstance?.getMap() ?? null}
        coordinates={poiPopup?.coordinates ?? null}
        onClose={closePoiPopup}
        markerIcon={
          poiPopup?.suggestedType && (
            <PlaceTypeIcon type={poiPopup.suggestedType} className="size-3 text-primary-foreground" />
          )
        }
      >
        {poiPopup && <POIContextMenu name={poiPopup.name} onAddPlace={handlePOIAddPlace} />}
      </MapPopup>

      {/* Pending place marker */}
      {placingContext?.coordinates && placingContext.type && (
        <MapMarker map={mapInstance?.getMap() ?? null} coordinates={placingContext.coordinates}>
          <PlaceTypeIcon type={placingContext.type} className="size-4 text-primary-foreground" />
        </MapMarker>
      )}

      {/* Add mode indicator */}
      {isPlacing && placingContext?.type && !placingContext.coordinates && (
        <Map.Messages>
          <MapHint onDismiss={cancelAddMode}>
            Click on the map to add a {PLACE_TYPE_META[placingContext.type].label.toLowerCase()}
          </MapHint>
        </Map.Messages>
      )}

      {/* Linking mode indicator */}
      {isLinking && linkingContext && !linkingContext.coordinates && (
        <Map.Messages>
          <MapHint onDismiss={cancelLinking}>Click an existing place, or click the map to create one</MapHint>
        </Map.Messages>
      )}

      {/* Place form for creating places (placing or linking mode) */}
      {(placingContext?.coordinates || linkingContext?.coordinates) && (
        <PlaceForm
          type={placingContext?.type ?? linkingContext?.allowedTypes?.[0]}
          allowedTypes={linkingContext?.allowedTypes}
          initialName={placingContext?.name ?? linkingContext?.name}
          onSubmit={placingContext?.coordinates ? handlePlaceSubmit : handleLinkingFormSubmit}
          onCancel={placingContext?.coordinates ? cancelAddMode : handleLinkingFormCancel}
          saving={savingPlace}
        />
      )}

      {/* Share dialog */}
      <ShareDialog
        map={adventureMap}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        onShareLinkCreated={(shareId) => patch({ shareId })}
      />

      {/* Collaborators dialog */}
      <CollaboratorsDialog
        mapId={adventureMap.id}
        canManageCollaborators={adventureMap.permissions.canManageCollaborators}
        open={collaboratorsDialogOpen}
        onOpenChange={setCollaboratorsDialogOpen}
      />

      <TransferOwnershipDialog
        open={transferDialogOpen}
        confirmation={adventureMap.name}
        onOpenChange={setTransferDialogOpen}
        onTransfer={handleMapTransfer}
      >
        The new owner will be in charge of this map. You&apos;ll stay on as an editor, and places you curated will still
        be yours.
      </TransferOwnershipDialog>

      {selectedPlace && (
        <TransferOwnershipDialog
          open={placeTransferDialogOpen}
          confirmation={selectedPlace.properties.name}
          onOpenChange={setPlaceTransferDialogOpen}
          onTransfer={handlePlaceTransfer}
        >
          The new owner will be in charge of this place. You won&apos;t be able to edit it anymore.
        </TransferOwnershipDialog>
      )}

      {/* Curator profile dialog */}
      <CuratorProfileDialog
        open={curatorDialogOpen}
        onOpenChange={setCuratorDialogOpen}
        saving={savingCurator}
        onSave={handleCuratorCreate}
        description="Before publishing this place to noma.to, let us know who you are. Your name will appear as the contributor."
      />
    </>
  );
}

function transferErrorMessage(err: unknown): string {
  if (isNotFoundError(err)) return "No account found for this email.";
  return getApiErrorMessage(err, "Transfer failed. Please try again.");
}
